import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { news } from "@/mocks/news";
import MovieCard from "@/components/feature/MovieCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

type Tab = "all" | "movie" | "news";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  const [keyword, setKeyword] = useState(q);
  const [tab, setTab] = useState<Tab>("all");

  useDocumentMeta(
    q ? `搜索“${q}” - 欧美三级影视大全` : "搜索 - 欧美三级影视大全",
    q
      ? `在欧美三级影视大全中搜索“${q}”的相关影片与资讯结果。`
      : "在欧美三级影视大全中搜索欧美三级电影与影视资讯。",
    "影视搜索,欧美三级搜索,影片搜索,资讯搜索"
  );

  const kw = q.trim().toLowerCase();

  const movieResults = useMemo(() => {
    if (!kw) return [];
    return movies.filter((m) =>
      [m.title, m.titleEn, m.director, m.cast, m.region, ...m.genres, ...m.tags, m.summary]
        .join(" ")
        .toLowerCase()
        .includes(kw)
    );
  }, [kw]);

  const newsResults = useMemo(() => {
    if (!kw) return [];
    return news.filter((n) =>
      [n.title, n.category, n.summary, ...n.tags, ...n.paragraphs].join(" ").toLowerCase().includes(kw)
    );
  }, [kw]);

  const totalCount = movieResults.length + newsResults.length;
  const recommended = movies.filter((m) => m.featured).slice(0, 4);

  const runSearch = () => {
    const next = new URLSearchParams();
    if (keyword.trim()) next.set("q", keyword.trim());
    setSearchParams(next);
    setTab("all");
  };

  const showMovies = tab === "all" || tab === "movie";
  const showNews = tab === "all" || tab === "news";

  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: "all", label: "全部", count: totalCount },
    { key: "movie", label: "电影", count: movieResults.length },
    { key: "news", label: "资讯", count: newsResults.length },
  ];

  return (
    <div className="mx-auto max-w-[1240px] px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb items={[{ label: "搜索" }]} />

      <header className="mt-5">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">搜索影视内容</h1>
        <p className="mt-2 text-sm text-foreground-600">
          输入关键词即可搜索影片名称、类型、导演、主演与影视资讯内容。
        </p>
      </header>

      <div className="mt-5 flex flex-col gap-2 rounded-lg border border-background-200/80 bg-background-50 p-2 sm:flex-row sm:items-center">
        <div className="flex flex-1 items-center gap-2 rounded-md bg-background-100 px-3.5 py-2.5">
          <i className="ri-search-line text-lg text-secondary-500"></i>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") runSearch();
            }}
            type="text"
            placeholder="搜索影片、导演、演员或资讯关键词"
            className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-secondary-500 focus:outline-none"
          />
        </div>
        <button
          type="button"
          onClick={runSearch}
          className="flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
        >
          <i className="ri-search-line"></i>
          搜索
        </button>
      </div>

      {q ? (
        <>
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-700">
              关键词“<span className="font-semibold text-primary-700">{q}</span>”共找到{" "}
              <span className="font-semibold text-primary-700">{totalCount}</span> 条结果
            </p>
            <span className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs text-secondary-700">
              演示范围内 · 前端模拟搜索
            </span>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={[
                  "flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
                  tab === t.key
                    ? "bg-primary-600 text-background-50"
                    : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700",
                ].join(" ")}
              >
                {t.label}
                <span className={tab === t.key ? "text-background-100/80" : "text-secondary-500"}>{t.count}</span>
              </button>
            ))}
          </div>

          {totalCount > 0 ? (
            <div className="mt-6 space-y-10">
              {showMovies && movieResults.length > 0 && (
                <section>
                  <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                    <i className="ri-movie-2-line text-primary-700"></i>
                    相关电影（{movieResults.length}）
                  </h2>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 md:gap-4">
                    {movieResults.map((m) => (
                      <MovieCard key={m.id} movie={m} />
                    ))}
                  </div>
                </section>
              )}

              {showNews && newsResults.length > 0 && (
                <section>
                  <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                    <i className="ri-newspaper-line text-primary-700"></i>
                    相关资讯（{newsResults.length}）
                  </h2>
                  <div className="space-y-3">
                    {newsResults.map((item) => (
                      <Link
                        key={item.id}
                        to={`/news-detail/${item.id}`}
                        className="group flex cursor-pointer flex-col gap-1.5 rounded-lg border border-background-200/80 bg-background-50 p-4 transition-colors hover:border-primary-300"
                      >
                        <div className="flex items-center gap-2">
                          <span className="rounded-md bg-accent-100 px-2 py-0.5 text-[11px] font-semibold text-accent-700">
                            {item.category}
                          </span>
                          <span className="text-xs text-secondary-500">{item.date}</span>
                        </div>
                        <h3 className="line-clamp-1 font-heading text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-700 md:text-base">
                          {item.title}
                        </h3>
                        <p className="line-clamp-2 text-sm leading-relaxed text-secondary-500">{item.summary}</p>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </div>
          ) : (
            <div className="mt-8">
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-3xl text-secondary-500">
                  <i className="ri-search-eye-line"></i>
                </span>
                <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">未找到与“{q}”相关的内容</h3>
                <p className="mt-2 max-w-md text-sm text-secondary-500">
                  换个关键词试试，比如影片名称、类型或导演。以下为你推荐一些热门影片。
                </p>
              </div>
              <section className="mt-8">
                <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-fire-line text-accent-600"></i>
                  为你推荐
                </h2>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 md:gap-4">
                  {recommended.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </section>
            </div>
          )}
        </>
      ) : (
        <div className="mt-8">
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-3xl text-secondary-500">
              <i className="ri-search-line"></i>
            </span>
            <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">开始你的搜索</h3>
            <p className="mt-2 max-w-md text-sm text-secondary-500">
              在上方输入关键词，搜索感兴趣的影片与影视资讯。试试“爱情”“惊悚”或某位导演的名字。
            </p>
          </div>
          <section className="mt-8">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-fire-line text-accent-600"></i>
              热门影片推荐
            </h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 md:gap-4">
              {recommended.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}